/**
 * The page tabs along the bottom of the board (docs/06-UI-SPEC.md §6.5, SRS C1).
 *
 * A board is one or more pages. The strip is how a teacher moves between them, adds
 * one, renames it, copies it, drags it into a different order, or takes it away.
 *
 * A student sees the same strip and can turn to a page, but cannot change what pages
 * the board has: that is the teacher's (SRS D4). While the class is following her,
 * turning a page on her board turns it on theirs.
 */

import { useState } from 'react';

import type { PageSummary } from '@opal/shared';

import type { PageActions } from '../../board/board-pages.js';
import { tooManyPages, PAGE_COUNT_WARNING_THRESHOLD } from '../../board/pages.js';
import { PlusIcon } from '../../assets/icons.js';
import { PageTabMenu } from './PageTabMenu.js';

export interface PageTabsProps {
  readonly pages: readonly PageSummary[];
  readonly activePageId: string | null;
  readonly actions: PageActions;
  /** SRS D4 — only the teacher may add, rename, reorder or delete pages. */
  readonly canManage: boolean;
}

interface MenuState {
  readonly pageId: string;
  readonly at: { readonly x: number; readonly y: number };
}

const TAB =
  'flex h-8 min-w-16 max-w-40 shrink-0 items-center rounded-md border px-3 text-sm transition-colors duration-fast';
const ACTIVE = 'border-primary bg-primary-subtle text-primary';
const RESTING = 'border-border bg-surface text-text-secondary hover:bg-surface-hover';

interface TabProps {
  readonly page: PageSummary;
  readonly active: boolean;
  readonly canManage: boolean;
  readonly renaming: boolean;
  readonly onOpen: () => void;
  readonly onRename: (name: string) => void;
  readonly onStartRename: () => void;
  readonly onMenu: (at: { x: number; y: number }) => void;
  readonly onDropOn: (pageId: string) => void;
}

/** A tab being renamed: type over the old name, Enter keeps it, Escape leaves it. */
function NameField({
  name,
  onRename,
}: {
  readonly name: string;
  readonly onRename: (name: string) => void;
}) {
  return (
    <input
      autoFocus
      defaultValue={name}
      aria-label="Page name"
      onFocus={(event) => {
        event.target.select();
      }}
      onBlur={(event) => {
        onRename(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key !== 'Escape') return;
        event.preventDefault();
        onRename(name);
      }}
      className={`${TAB} border-primary bg-surface text-text-primary outline-none`}
    />
  );
}

/** One tab: the page's name, or a field to type a new one into. */
function Tab(props: TabProps) {
  const { page, active, canManage, renaming } = props;
  if (renaming) return <NameField name={page.name} onRename={props.onRename} />;

  return (
    <button
      type="button"
      aria-current={active}
      title={page.name}
      draggable={canManage}
      onClick={props.onOpen}
      onDoubleClick={() => {
        if (canManage) props.onStartRename();
      }}
      onContextMenu={(event) => {
        if (!canManage) return;
        event.preventDefault();
        props.onMenu({ x: event.clientX, y: event.clientY });
      }}
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', page.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(event) => {
        if (canManage) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        props.onDropOn(event.dataTransfer.getData('text/plain'));
      }}
      className={`${TAB} ${active ? ACTIVE : RESTING}`}
    >
      <span className="truncate">{page.name}</span>
    </button>
  );
}

/** SRS C1 — unlimited pages, with a word of warning once there are a lot of them. */
function TooMany({ count }: { readonly count: number }) {
  return (
    <span className="shrink-0 pl-2 text-xs text-warning">
      {count} pages. Past {PAGE_COUNT_WARNING_THRESHOLD} a board gets slow to save —
      consider starting a new one.
    </span>
  );
}

/** The + at the end of the strip (docs/06-UI-SPEC.md §6.5). */
function AddPage({ onAdd }: { readonly onAdd: () => void }) {
  return (
    <button
      type="button"
      aria-label="Add a page"
      title="Add a page"
      onClick={() => {
        onAdd();
      }}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-text-primary transition-colors duration-fast hover:bg-surface-hover"
    >
      <PlusIcon size={16} />
    </button>
  );
}

/** The menu a right-click opens, with what each of its three items does. */
function TabMenu({
  menu,
  actions,
  lastPage,
  onRename,
  onClose,
}: {
  readonly menu: MenuState;
  readonly actions: PageActions;
  /** A board must always have a page, so the last one cannot be deleted. */
  readonly lastPage: boolean;
  readonly onRename: (pageId: string) => void;
  readonly onClose: () => void;
}) {
  return (
    <PageTabMenu
      at={menu.at}
      onRename={() => {
        onRename(menu.pageId);
        onClose();
      }}
      onDuplicate={() => {
        actions.duplicate(menu.pageId);
        onClose();
      }}
      onDelete={
        lastPage
          ? null
          : () => {
              actions.remove(menu.pageId);
              onClose();
            }
      }
      onClose={onClose}
    />
  );
}

interface StripProps extends PageTabsProps {
  readonly renaming: string | null;
  readonly onRenaming: (pageId: string | null) => void;
  readonly onMenu: (menu: MenuState) => void;
}

/** The tabs themselves, in the order the pages are in. */
function Strip({ pages, activePageId, actions, canManage, ...on }: StripProps) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
      {pages.map((page, index) => (
        <Tab
          key={page.id}
          page={page}
          active={page.id === activePageId}
          canManage={canManage}
          renaming={on.renaming === page.id}
          onOpen={() => {
            actions.goTo(page.id);
          }}
          onStartRename={() => {
            on.onRenaming(page.id);
          }}
          onRename={(name) => {
            on.onRenaming(null);
            actions.rename(page.id, name);
          }}
          onMenu={(at) => {
            on.onMenu({ pageId: page.id, at });
          }}
          onDropOn={(dragged) => {
            if (dragged !== '' && dragged !== page.id) actions.move(dragged, index);
          }}
        />
      ))}
    </div>
  );
}

export function PageTabs(props: PageTabsProps) {
  const { pages, actions, canManage } = props;
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  if (pages.length === 0) return null;

  return (
    <div className="flex h-tabs shrink-0 items-center gap-2 border-t border-border bg-surface px-3">
      <Strip {...props} renaming={renaming} onRenaming={setRenaming} onMenu={setMenu} />
      {tooManyPages(pages.length) ? <TooMany count={pages.length} /> : null}
      {canManage ? (
        <AddPage
          onAdd={() => {
            actions.add();
          }}
        />
      ) : null}
      {menu ? (
        <TabMenu
          menu={menu}
          actions={actions}
          lastPage={pages.length <= 1}
          onRename={setRenaming}
          onClose={() => {
            setMenu(null);
          }}
        />
      ) : null}
    </div>
  );
}
